import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { listMetaAccounts, disconnectMetaAccount, type MetaAccount } from '../../services/meta-account-api.js';

@customElement('cp-meta-accounts-settings')
export class CpMetaAccountsSettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _accounts: MetaAccount[] = [];
  @state() private _loading = true;
  @state() private _error = '';
  @state() private _connectAlias = '';

  connectedCallback(): void {
    super.connectedCallback();
    this._loadAccounts();

    // Check URL params for OAuth callback status
    const params = new URLSearchParams(window.location.search);
    if (params.get('meta_connected') === 'true') {
      this._loadAccounts();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    const metaError = params.get('meta_error');
    if (metaError) {
      this._error = `Meta-Verbindung fehlgeschlagen: ${metaError}`;
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  private async _loadAccounts(): Promise<void> {
    this._loading = true;
    try {
      this._accounts = await listMetaAccounts();
      this._error = '';
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private _handleConnect(): void {
    const alias = this._connectAlias.trim();
    if (!alias) return;
    // Navigate to OAuth flow
    window.location.href = `/api/meta-accounts/start?alias=${encodeURIComponent(alias)}`;
  }

  private async _handleDisconnect(account: MetaAccount): Promise<void> {
    if (!confirm(`Meta-Account "${account.account_alias}" trennen?`)) return;
    try {
      await disconnectMetaAccount(account.id);
      await this._loadAccounts();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Trennen';
    }
  }

  private _handleReconnect(account: MetaAccount): void {
    window.location.href = `/api/meta-accounts/start?alias=${encodeURIComponent(account.account_alias)}`;
  }

  render() {
    return html`
      <div class="cp-meta-accounts">
        <h3 class="cp-meta-accounts__title">Meta / Instagram Accounts</h3>
        <p class="cp-meta-accounts__desc">Verbinde Meta-Accounts fuer Instagram Insights und Graph API.</p>

        ${this._error ? html`<p style="color: var(--color-danger, #ff3b30); font-size: var(--font-size-sm);">${this._error}</p>` : nothing}

        ${this._loading ? html`<p class="cp-meta-accounts__empty">Laden...</p>` : html`
          <div class="cp-meta-accounts__list">
            ${this._accounts.length === 0
              ? html`<p class="cp-meta-accounts__empty">Noch keine Meta-Accounts verbunden.</p>`
              : this._accounts.map((account) => html`
                <div class="cp-meta-accounts__item">
                  <span class="cp-meta-accounts__status-dot ${account.needs_reauth ? 'cp-meta-accounts__status-dot--reauth' : 'cp-meta-accounts__status-dot--ok'}"></span>
                  <div class="cp-meta-accounts__item-info">
                    <div class="cp-meta-accounts__item-alias">${account.account_alias}</div>
                    <div class="cp-meta-accounts__item-email">Meta ID: ${account.meta_user_id}</div>
                  </div>
                  <div class="cp-meta-accounts__item-actions">
                    ${account.needs_reauth ? html`
                      <button class="cp-meta-accounts__btn" @click=${() => this._handleReconnect(account)}>Erneut verbinden</button>
                    ` : nothing}
                    <button class="cp-meta-accounts__btn cp-meta-accounts__btn--danger" @click=${() => this._handleDisconnect(account)}>Trennen</button>
                  </div>
                </div>
              `)
            }
          </div>

          <div class="cp-meta-accounts__connect">
            <input
              class="cp-meta-accounts__connect-input"
              type="text"
              placeholder="Alias (z.B. rockstar, business)"
              .value=${this._connectAlias}
              @input=${(e: Event) => { this._connectAlias = (e.target as HTMLInputElement).value; }}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this._handleConnect(); }}
            />
            <button class="cp-meta-accounts__btn cp-meta-accounts__btn--primary" @click=${this._handleConnect}>
              Account verbinden
            </button>
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-meta-accounts-settings': CpMetaAccountsSettings;
  }
}
